import type { CSSProperties } from 'react';
import {
  type PlayerIndex,
  type ScoreState,
} from '@aiscoreboard/contracts';
import { BreakHistory } from './BreakHistory';
import { CalculatorIcon, TrophyIcon } from './Icons';
import { Flag } from './CountryPicker';
import { accentOf, frameVars, QUIET_FRAME } from '../lib/accent';
import { useT } from '../lib/i18n';

interface Props {
  state: ScoreState;
  player: PlayerIndex;
  /**
   * The signed-in player's picture, when there is one.
   *
   * A guest has no account and a signed-in player may never have uploaded a
   * photograph, and in both cases the name goes back to being centred on its
   * own. A reserved-but-empty box would be a hole in the plate that says
   * something is missing, when nothing is.
   */
  avatarUrl?: string | null;
  /**
   * Hand the table to this player. Given only when there is somebody to hand
   * it *from*, so the panel does not have to know the rules of the switch.
   */
  onTakeTable?: () => void;
  onAddScore?: () => void;
}

/**
 * One player's side of the board: name, points, and their break log.
 *
 * The points are the largest thing on the screen because they are the number
 * the frame is actually about, and they are set in the mono face like every
 * other figure here — a scoreboard's numbers change while somebody is looking
 * at them, and fixed advance widths mean a score can go from 8 to 88 to 138
 * without the panel shuffling around it.
 *
 * Each player gets a colour and it is theirs for the whole match: blue on the
 * left, amber on the right. A scoreboard is read from across a room, often out
 * of the corner of an eye, and colour arrives before shape does, so a glance
 * says *whose* number grew before a single digit has been read.
 */
export function PlayerPanel({ state, player, avatarUrl, onTakeTable, onAddScore }: Props) {
  const t = useT();
  const active = state.activePlayer === player;
  const name = state.players[player];
  const country = state.playerCountries[player];
  const score = state.scores[player];
  const accent = accentOf(player);
  // Tapping the idle player's panel is the same instruction as the switch key
  // on the bar: it is where a hand goes when the operator is thinking "it is
  // *his* turn now" rather than "switch". The player at the table is not a
  // target — there is nothing for it to mean, and a live panel that can be
  // pressed is a panel somebody will press by accident.
  const canTake = !active && onTakeTable !== undefined;

  const hasBreak = state.highestBreak[player] > 0;

  // Only the player who conceded the points carries the line.
  const handicapGiven = state.handicap?.from === player ? state.handicap.points : 0;

  // Whoever is at the table gets a lit edge and a soft ring; the idle panel
  // keeps a plain hairline. One signal, carried by the border, rather than a
  // bloom around the whole block — a panel that glows is a panel pretending to
  // emit light, which is the single thing that made this board read as science
  // fiction rather than as software.
  const frame = frameVars(accent.rgb, active) as CSSProperties;
  const calculator = onAddScore && (
    <button
      type="button"
      data-score-entry={player}
      aria-label={t('scoreEntry.open', { name })}
      title={t('scoreEntry.open', { name })}
      disabled={state.paused || state.status !== 'in_progress'}
      onClick={event => { event.stopPropagation(); onAddScore(); }}
      onKeyDown={event => event.stopPropagation()}
      className="btn-ghost flex h-12 w-12 shrink-0 items-center justify-center rounded-full p-2 text-white/70 hover:text-white sm:h-14 sm:w-14"
    >
      <CalculatorIcon className="h-8 w-8 sm:h-9 sm:w-9" />
    </button>
  );

  return (
    <div
      data-player-panel={player}
      role={canTake ? 'button' : undefined}
      tabIndex={canTake ? 0 : undefined}
      aria-label={canTake ? t('match.takeTable', { name }) : undefined}
      title={canTake ? t('match.takeTable', { name }) : undefined}
      onClick={canTake ? event => {
        if (!(event.target instanceof Element && event.target.closest('button'))) onTakeTable?.();
      } : undefined}
      onKeyDown={
        canTake
          ? (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onTakeTable?.();
              }
            }
          : undefined
      }
      className={`hud-frame flex min-h-0 flex-1 flex-col items-center gap-3 p-5 outline-none
                  transition ${
                    // The whole card is the target, not the digits on it. "It
                    // is his turn now" is a thought about a *player*, and the
                    // hand that acts on it goes to the player's half of the
                    // board — a name, a photograph, a colour — not to a
                    // rectangle around the score that has to be found first.
                    // The score was the target for as long as this was
                    // pressable and it was the one part of the panel an
                    // operator was least likely to aim at.
                    canTake ? 'cursor-pointer focus-visible:ring-2 focus-visible:ring-white/40' : ''
                  }`}
      style={frame}
    >
      {/* Whose shot it is, in one line that can be read across a room. The
          box keeps its height when the badge is not in it, so the two panels
          stay level and nothing under them moves on a switch of turn. */}
      <div className="flex h-7 w-full shrink-0 items-center justify-center">
        {active && (
          <div className="flex items-center gap-2">
            <span className="turn-badge" style={{ '--turn-rgb': accent.rgb } as CSSProperties}>
              <span className="turn-dot" />
              {t('match.atTable')}
            </span>
            {state.freeBall && (
              <span className="turn-badge" style={{ '--turn-rgb': '245,197,24' } as CSSProperties}>
                <span className="turn-dot" />
                {t('match.freeBall')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* The name in a centred plate, with mirrored calculator slots.
          The QR code used to sit in this corner and the name was padded
          away from it, which left every name off centre by an amount that
          depended on which side of the board it was on.

          There used to be a "Player 1" / "Player 2" caption above this plate,
          on the theory that a fixed seat label is what tells a spectator which
          half is which. It is not: the name is. Once somebody has signed in,
          the caption said the same thing as the plate underneath it only less
          usefully, and in German it said it at a length ("Spieler 1") that
          made the top of the panel busier than the score. The dot that lived
          beside it is gone with it — who is at the table is already said by
          the lit edge, by the plate's own colour and by the idle score
          dimming, which is two signals more than the question needs.

          The plate is neutral, not tinted. It sits inside a panel that already
          carries the player's colour, and glass over glass over the same tint
          stops looking translucent and starts looking like a solid swatch —
          the one thing a frosted surface must not do.

          When a player has a photograph it sits inside the plate, on the
          outside edge of the board: to the right of the name on the left-hand
          panel, to the left of it on the right-hand one. The two faces end up
          at the outer corners, looking in across the score between them, which
          is how the two players are actually arranged at the table. Putting
          both on the same side would have made the pair read as a list. A
          guest, or a player who never uploaded a picture, keeps the plain
          centred name — there is nothing to reserve room for. */}
      <div className="flex w-full min-w-0 shrink-0 items-center justify-center gap-2">
        {onAddScore && <span className="w-12 shrink-0 sm:w-14">{player === 1 && calculator}</span>}
        <div
          data-player-name
          title={name}
          className={`hud-frame hud-frame-sm flex min-w-0 max-w-full items-center gap-3 px-4 py-2 ${
            avatarUrl && player === 1 ? 'flex-row-reverse' : 'flex-row'
          }`}
          style={QUIET_FRAME as CSSProperties}
        >
          <span
            className="block min-w-0 truncate text-center text-2xl font-semibold leading-tight
                       tracking-[0.01em] text-white sm:text-[2rem]"
          >
            <span className="inline-flex max-w-full items-center gap-2 align-middle">
              {player === 0 && country && <Flag code={country} className="h-5 w-7" />}
              <span className="min-w-0 truncate">{name}</span>
              {player === 1 && country && <Flag code={country} className="h-5 w-7" />}
            </span>
          </span>
          {avatarUrl && (
            <img
              src={avatarUrl}
              alt=""
              className="h-10 w-10 shrink-0 rounded-full object-cover sm:h-12 sm:w-12"
              style={{ boxShadow: `0 0 0 2px rgba(${accent.rgb},0.55)` }}
            />
          )}
        </div>
        {onAddScore && <span className="w-12 shrink-0 sm:w-14">{player === 0 && calculator}</span>}
      </div>

      {/* The handicap, in the panel of the player who conceded it, directly
          under their name — so the two halves of the sentence are in reading
          order: the plate says who, the line under it says what they gave and
          to whom. It was a banner across the top of the board for a while,
          which stated the fact but detached it from either player; a
          spectator then had to match two names in a strip against two panels
          before it meant anything. Here the giver is simply the card it is
          written on.

          It is gold, and it is the only gold on the panel until somebody
          makes a break, because it is the one thing on this card that
          explains a score that would otherwise look wrong. */}
      {handicapGiven > 0 && (
        <div
          className="hud-frame hud-frame-sm flex max-w-full shrink-0 items-center justify-center
                     gap-2 whitespace-nowrap px-4 py-1.5 text-base font-semibold leading-none
                     sm:gap-2.5 sm:text-xl"
          style={
            {
              ...QUIET_FRAME,
              background: 'rgba(245, 197, 24, 0.10)',
              boxShadow: 'inset 0 0 0 1px rgba(245, 197, 24, 0.35)',
            } as CSSProperties
          }
        >
          <span className="text-white/60">{t('match.handicapVerb')}</span>
          <span
            className="min-w-0 truncate"
            style={{ color: accentOf(player === 0 ? 1 : 0).hex }}
            title={state.players[player === 0 ? 1 : 0]}
          >
            {state.players[player === 0 ? 1 : 0]}
          </span>
          <span className="num shrink-0 text-sys-yellow">
            {t('match.handicapPoints', { n: handicapGiven })}
          </span>
        </div>
      )}

      {/* Nothing between the plate and the score. There used to be a hairline
          here — and a diamond on it, and a ladder of gauge ticks either side
          of the number — on the theory that an instrument reads as an
          instrument because of the marks that are *not* readouts. That is true
          of instruments, and this is not one. The rule that survived has moved
          down to sit above the footer, where it separates two unlike things
          instead of two parts of the same one. */}

      {/* The score takes the whole band between the name plate and the rule
          above the footer, and centres itself in it. `.fit-box` is a size
          container that centres what it holds, so the number sits in the
          middle of that band rather than riding above the break log — at the
          start of a frame the empty space under the score was half the panel,
          and the one figure the room is reading was nowhere near the middle of
          the box it was in.

          It measures itself against that box rather than against the viewport,
          so a three-figure score shrinks to fit sideways instead of
          overflowing. The ceiling is deliberately below the height available —
          a score set to the full box crowded the name plate above it and the
          readout below, and the number was never hard to read.

          Two digits minimum. A frame opens at nought and a lone `0` in a box
          this size is a hole in the panel, while `00` is a *readout at rest* —
          the same thing a dressing-room clock or a stadium board does, and for
          the same reason: the shape of the display should not change the
          moment the first point is scored. Above nine it is simply the score.

          Both scores keep their player's colour whether or not that player is
          at the table. Colour here is identity, not state: the moment the idle
          score went white, the two halves of the board stopped being a pair of
          comparable figures and became "the live one and the other one". */}
      <div className="fit-box w-full min-h-0 flex-1 rounded-3xl">
        <span
          className="num-lg block leading-none transition-opacity"
          style={{
            fontSize: `min(64cqh, ${Math.min(40, 160 / String(score).padStart(2, '0').length)}cqw)`,
            color: accent.hex,
            opacity: active ? 1 : 0.62,
            letterSpacing: '-0.04em',
          }}
        >
          {String(score).padStart(2, '0')}
        </span>
      </div>

      {/* Three rows of chips, always. The reservation is the point: the score
          above it is the one figure the room is watching, and a strip that
          grew a row every few pots slid that figure up and down the panel
          while somebody was reading it. A fixed block means the number is
          nailed to one place for the whole frame and the log fills into the
          space underneath it.

          Three rows rather than four because the score and the reservation are
          centred in the band *together*, and the fourth row was buying a
          length of empty panel most of every frame at the price of the number
          above it. 1.4rem a chip, 0.25rem between them and 0.5rem of padding
          is 5.2rem — an exact three, with no ambiguous half-row at the bottom
          edge to make a spectator wonder whether something is cut off. Past
          three rows the strip scrolls. */}
      <div className="w-full h-[5.2rem] shrink-0">
        <BreakHistory visits={state.visits} player={player} />
      </div>

      {/* A hairline, and under it the player's own record for the frame.

          The rule used to sit under the name plate. It is better here: what it
          has to do is cut the footer off from everything above it, which is
          the one place on this panel where two unlike things meet, and it
          leaves the record sitting on the bottom edge of the card where a
          footer belongs. It carries the player's colour at a fraction of a
          percent, so the panel is divided without a grey line being drawn
          across a coloured card. */}
      <div
        aria-hidden
        className="h-px w-full shrink-0"
        style={{
          background: `linear-gradient(to right, transparent, rgba(${accent.rgb},${
            active ? 0.4 : 0.18
          }) 15%, rgba(${accent.rgb},${active ? 0.4 : 0.18}) 85%, transparent)`,
        }}
      />

      {/* Frames won used to sit here, one half of the match score in each
          panel — two feet apart, which is not how anybody reads a pair. It is
          one block in the middle now, level with the two name plates. What is
          left is the player's own record for the frame: their highest break,
          which belongs to them and to nobody else.

          It counts the break in progress. `highestBreak` used to be sealed
          only when a visit ended, so a player could be forty into a fifty and
          the readout would still show whatever they had made an hour ago —
          the one moment the number is worth watching is the one moment it was
          wrong. It now climbs with the current break, ball by ball, and simply
          stops climbing when the player misses. */}
      {/* Gold, and only once there is something to be gold about. A frame
          record is the one figure on this panel that is an achievement rather
          than a state, and trophy-gold is the colour every sport uses for
          that. At nought it stays neutral: a lit zero is a medal for nothing,
          and it would pull the eye to the emptiest number on the board. */}
      <div className="flex w-full shrink-0 items-center justify-center gap-2.5 pt-2">
        <TrophyIcon
          className={`h-5 w-5 shrink-0 sm:h-6 sm:w-6 ${
            hasBreak ? 'text-sys-yellow/75' : 'text-white/40'
          }`}
        />
        <span className="hud-label">{t('match.highLabel')}</span>
        <span
          className={`num text-lg font-semibold leading-none sm:text-xl ${
            hasBreak ? 'text-sys-yellow' : 'text-white/80'
          }`}
        >
          {state.highestBreak[player]}
        </span>
      </div>
    </div>
  );
}
